import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Card, CardContent, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';

import StandaloneMultiProductOptionSelect from '../PromoV3/StandaloneMultiProductOptionSelect';

const PromoDiscountCards = ({
  value,
  onChange,
  promoMeta,
  showAddButton,
  promoType,
  highlightedFields,
  resetHighlightedField,
}) => {
  const predefinedDiscounts = promoType === 'percentage' ? [5, 10, 15, 20] : [50, 100, 250];

  const [discounts, setDiscounts] = useState(value || []);

  useEffect(() => {
    setDiscounts(value || []);
  }, [value]);

  const handleAddDiscount = () => {
    const newId = Math.max(...discounts.map((d) => d.id || 0), 0) + 1;
    const newDiscount = {
      id: newId,
      discountValue: predefinedDiscounts[1],
      products: [],
    };
    const updatedDiscounts = [...discounts, newDiscount];
    setDiscounts(updatedDiscounts);
    if (updatedDiscounts.length == 0) {
      resetHighlightedField(`add_discounts`);
    }
    onChange(updatedDiscounts);
  };

  const handleDeleteDiscount = (id) => {
    const updatedDiscounts = discounts.filter((d) => d.id !== id);
    setDiscounts(updatedDiscounts);
    onChange(updatedDiscounts);
  };

  const handleUpdateDiscount = (id, field, newValue) => {
    const updatedDiscounts = discounts.map((d) => (d.id === id ? { ...d, [field]: newValue } : d));
    setDiscounts(updatedDiscounts);
    onChange(updatedDiscounts);
  };

  useEffect(() => {
    const defaultDiscountValue = predefinedDiscounts[1];

    const updatedDiscounts = discounts.map((d) => ({
      ...d,
      discountValue: defaultDiscountValue,
    }));

    setDiscounts(updatedDiscounts);
    onChange(updatedDiscounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoType]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Discounts</Typography>
      </Box>

      <Stack spacing={2}>
        {discounts.map((discount, index) => {
          const isOther = !predefinedDiscounts.includes(discount.discountValue);

          return (
            <Card key={discount.id} variant="outlined" data-discount-id={discount.id}>
              <CardContent
                sx={{
                  ...(highlightedFields.some((hf) => hf === `d-${discount.id}`) && {
                    p: 1,
                    border: '2px solid #d32f2f',
                    borderRadius: 1,
                    backgroundColor: '#ffebee',
                  }),
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">Discount #{index + 1}</Typography>
                    <IconButton onClick={() => handleDeleteDiscount(discount.id)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {predefinedDiscounts.map((val) => (
                      <Chip
                        key={val}
                        label={promoType === 'percentage' ? `${val}%` : `$${val}`}
                        variant={discount.discountValue === val ? 'filled' : 'outlined'}
                        color={discount.discountValue === val ? 'primary' : 'default'}
                        onClick={() => handleUpdateDiscount(discount.id, 'discountValue', val)}
                      />
                    ))}
                    <Chip
                      label="Other"
                      variant={isOther ? 'filled' : 'outlined'}
                      color={isOther ? 'primary' : 'default'}
                      onClick={() => handleUpdateDiscount(discount.id, 'discountValue', 0)}
                    />
                  </Stack>

                  {/* Custom Value Field if "Other" */}
                  {isOther && (
                    <TextField
                      label="Custom Discount Value"
                      type="number"
                      inputProps={{
                        min: 0,
                      }}
                      value={discount.discountValue || ''}
                      onChange={(e) =>
                        handleUpdateDiscount(discount.id, 'discountValue', parseFloat(e.target.value) || 0)
                      }
                      size="small"
                    />
                  )}

                  {/* Product Selector */}
                  <StandaloneMultiProductOptionSelect
                    id={`products-${discount.id}`}
                    multiSelectType="products"
                    value={discount.products || []}
                    defaultValue={discount.products || []}
                    promoProductTypes={promoMeta?.promo_product_types || []}
                    onChange={(newValue) => handleUpdateDiscount(discount.id, 'products', newValue)}
                    showUpToXProducts={5}
                  />
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {showAddButton && (
          <Box
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              ...(highlightedFields.some((hf) => hf === `add_discounts`) && {
                p: 1,
                border: '2px solid #d32f2f',
                borderRadius: 1,
                backgroundColor: '#ffebee',
              }),
            }}
          >
            <Button
              data-add-discounts-button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddDiscount}
              size="small"
              color={highlightedFields.some((hf) => hf === `add_discounts`) ? 'error' : 'primary'}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
              }}
            >
              Add another Discount
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PromoDiscountCards;
